"use client";

/**
 * 列表页/首页的「筛选目录」客户端组件，取代旧 PublicPage 的 hydrateXxx 系列
 * （dangerouslySetInnerHTML + DOM 查询挂事件）。全部数据由服务端 SSR 传入，
 * 这里只用 useState 做纯前端过滤，DOM 结构 / class 名与改造前保持一致。
 */
import { useMemo, useState } from "react";
import { ParkCard, OpcCard, ProductCard, PolicyCard, ActivityCard, InvestCard } from "./cards";
import type { ParkItem, ProvinceCount } from "@/common/interface/park";
import type { OpcItem, OpcProduct } from "@/common/interface/opc";
import type { PolicyItem } from "@/common/interface/policy";
import type { ActivityItem } from "@/common/interface/activity";
import type { InvestOpportunity } from "@/common/interface/invest";
import { sortByDateDesc, provinceCounts, POLICY_ALL_TAB } from "@/common/policies/render";
import {
  sortActivities,
  facetCounts,
  ACTIVITY_ALL_REGION,
  ACTIVITY_ALL_CATEGORY,
} from "@/common/activities/render";
import {
  sortInvest,
  typeLabels,
  regionLabels,
  INVEST_ALL_TYPE,
  INVEST_ALL_REGION,
} from "@/common/invest/render";

const uniq = (values: string[]): string[] => [...new Set(values.filter(Boolean))];

/** 园区目录：省 → 市 两级筛选（首页 capped 时仅展示前 6 张）。找园区页与首页共用。 */
export function ParkDirectory({
  parks,
  provinces,
  capped = false,
}: {
  parks: ParkItem[];
  provinces: ProvinceCount[];
  capped?: boolean;
}) {
  const ALL = "全部";
  const [province, setProvince] = useState(ALL);
  const [city, setCity] = useState(ALL);

  const cities = useMemo(() => {
    if (province === ALL) return [];
    const out: string[] = [];
    parks.forEach((p) => {
      if (p.province === province && p.city && !out.includes(p.city)) out.push(p.city);
    });
    return out;
  }, [parks, province]);

  const filtered = parks.filter(
    (p) =>
      (province === ALL || p.province === province) &&
      (city === ALL || p.city === city)
  );
  const shown = capped ? filtered.slice(0, 6) : filtered;

  const pickProvince = (p: string) => {
    setProvince(p);
    setCity(ALL);
  };

  return (
    <>
      <div className="park-directory-tabs">
        {[ALL, ...provinces.map((p) => p.province)].map((label) => (
          <button
            key={label}
            data-city={label}
            className={label === province ? "active" : ""}
            onClick={() => pickProvince(label)}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="park-city-tabs" style={{ display: province === ALL ? "none" : undefined }}>
        {province !== ALL &&
          [ALL, ...cities].map((label) => (
            <button
              key={label}
              data-city={label}
              className={label === city ? "active" : ""}
              onClick={() => setCity(label)}
            >
              {label}
            </button>
          ))}
      </div>
      <section className={capped ? "park-directory-grid park-directory-grid--capped" : "park-directory-grid"}>
        {shown.length ? (
          shown.map((p) => <ParkCard key={p.slug} park={p} />)
        ) : (
          <p className="park-empty">园区信息完善中</p>
        )}
      </section>
    </>
  );
}

/** 政策目录：仅省份一级 tab（「全国」即展示全部）+ 卡片网格。 */
export function PolicyDirectory({ policies, capped = false }: { policies: PolicyItem[]; capped?: boolean }) {
  const [province, setProvince] = useState(POLICY_ALL_TAB);
  const sorted = useMemo(() => sortByDateDesc(policies), [policies]);
  const provinces = useMemo(() => provinceCounts(sorted), [sorted]);
  const filtered = sorted.filter((p) => province === POLICY_ALL_TAB || p.province === province);
  const shown = capped ? filtered.slice(0, 6) : filtered;

  return (
    <>
      <div className="park-directory-tabs">
        {[POLICY_ALL_TAB, ...provinces.map((p) => p.province)].map((label) => (
          <button
            key={label}
            data-city={label}
            className={label === province ? "active" : ""}
            onClick={() => setProvince(label)}
          >
            {label}
          </button>
        ))}
      </div>
      <section className="policy-directory-grid">
        {shown.length ? (
          shown.map((p) => <PolicyCard key={p.url} policy={p} />)
        ) : (
          <p className="park-empty">政策信息完善中</p>
        )}
      </section>
    </>
  );
}

/** 活动目录：地区 × 类型两维交叉筛选 + 工具栏计数 + 卡片网格。 */
export function ActivityDirectory({ activities }: { activities: ActivityItem[] }) {
  const [region, setRegion] = useState(ACTIVITY_ALL_REGION);
  const [category, setCategory] = useState(ACTIVITY_ALL_CATEGORY);
  const sorted = useMemo(() => sortActivities(activities), [activities]);
  const regions = useMemo(() => facetCounts(sorted, "city").map((f) => f.label), [sorted]);
  const categories = useMemo(() => facetCounts(sorted, "category").map((f) => f.label), [sorted]);
  const filtered = sorted.filter(
    (a) =>
      (region === ACTIVITY_ALL_REGION || a.city === region) &&
      (category === ACTIVITY_ALL_CATEGORY || a.category === category)
  );

  return (
    <>
      <div className="activity-filter">
        <div>
          <b>活动地区</b>
          {[ACTIVITY_ALL_REGION, ...regions].map((label) => (
            <button
              key={label}
              data-region={label}
              className={label === region ? "active" : ""}
              onClick={() => setRegion(label)}
            >
              {label}
            </button>
          ))}
        </div>
        <div>
          <b>活动类型</b>
          {[ACTIVITY_ALL_CATEGORY, ...categories].map((label) => (
            <button
              key={label}
              data-category={label}
              className={label === category ? "active" : ""}
              onClick={() => setCategory(label)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="activity-filter-toolbar">
          <h2>
            全部活动 <small>共 {filtered.length} 场</small>
          </h2>
        </div>
      </div>
      <section className="activity-card-grid">
        {filtered.length ? (
          filtered.map((a) => <ActivityCard key={a.url} activity={a} />)
        ) : (
          <p className="park-empty">活动信息完善中</p>
        )}
      </section>
    </>
  );
}

/** 投资目录：机会类型 × 适用地区两维交叉筛选 + 条数计数 + 卡片列表。 */
export function InvestDirectory({ list }: { list: InvestOpportunity[] }) {
  const [type, setType] = useState(INVEST_ALL_TYPE);
  const [region, setRegion] = useState(INVEST_ALL_REGION);
  const sorted = useMemo(() => sortInvest(list), [list]);
  const types = useMemo(() => typeLabels(sorted), [sorted]);
  const regions = useMemo(() => regionLabels(sorted), [sorted]);
  const filtered = sorted.filter(
    (i) =>
      (type === INVEST_ALL_TYPE || i.typeLabel === type) &&
      (region === INVEST_ALL_REGION || i.region === region)
  );

  return (
    <>
      <div className="invest-filter public-invest-filter">
        <div>
          <b>机会类型</b>
          {types.map((label) => (
            <button
              key={label}
              data-type={label}
              className={label === type ? "active" : ""}
              onClick={() => setType(label)}
            >
              {label}
            </button>
          ))}
        </div>
        <div>
          <b>适用地区</b>
          {regions.map((label) => (
            <button
              key={label}
              data-region={label}
              className={label === region ? "active" : ""}
              onClick={() => setRegion(label)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="public-opportunity-list">
        {filtered.length ? (
          filtered.map((i) => <InvestCard key={i.slug} item={i} />)
        ) : (
          <p className="park-empty">投资活动信息完善中</p>
        )}
      </div>
    </>
  );
}

/** 找 OPC：OPC / 产品两个大 tab + 每个 pane 内单维度筛选。 */
export function OpcDirectory({
  opcs,
  products,
}: {
  opcs: OpcItem[];
  products: OpcProduct[];
}) {
  const [tab, setTab] = useState<"opc" | "product">("opc");
  const [opcCity, setOpcCity] = useState("");
  const [opcIndustry, setOpcIndustry] = useState("");
  const [productCategory, setProductCategory] = useState("");

  const opcCities = useMemo(() => uniq(opcs.map((o) => o.city)), [opcs]);
  const opcIndustries = useMemo(() => uniq(opcs.map((o) => o.industry)), [opcs]);
  const productCategories = useMemo(() => uniq(products.map((p) => p.category)), [products]);

  const filteredOpcs = opcs.filter(
    (o) => (!opcCity || o.city === opcCity) && (!opcIndustry || o.industry === opcIndustry)
  );
  const filteredProducts = products.filter((p) => !productCategory || p.category === productCategory);

  return (
    <>
      <div className="directory-major-tabs">
        <button className={tab === "opc" ? "active" : ""} data-tab="opc" onClick={() => setTab("opc")}>
          OPC
        </button>
        <button
          className={tab === "product" ? "active" : ""}
          data-tab="product"
          onClick={() => setTab("product")}
        >
          产品
        </button>
      </div>
      <div className="major-pane" data-pane="opc" style={{ display: tab === "opc" ? undefined : "none" }}>
        <div className="filter-panel opc-filter">
          <FilterRow title="城市" field="city" values={opcCities} value={opcCity} onPick={setOpcCity} />
          <FilterRow title="方向" field="industry" values={opcIndustries} value={opcIndustry} onPick={setOpcIndustry} />
        </div>
        <div className="results-head">
          <h2>
            OPC 主体 <small>共 {opcs.length} 家一人公司</small>
          </h2>
        </div>
        <section className="opc-grid">
          {filteredOpcs.length ? (
            filteredOpcs.map((o) => <OpcCard key={o.id} opc={o} products={products} />)
          ) : (
            <p className="opc-empty">OPC 信息完善中</p>
          )}
        </section>
      </div>
      <div
        className="major-pane"
        data-pane="product"
        style={{ display: tab === "product" ? undefined : "none" }}
      >
        <div className="filter-panel product-filter">
          <FilterRow
            title="分类"
            field="category"
            values={productCategories}
            value={productCategory}
            onPick={setProductCategory}
          />
        </div>
        <div className="results-head">
          <h2>
            OPC 产品 <small>共 {products.length} 个</small>
          </h2>
        </div>
        <section className="opc-grid product-grid">
          {filteredProducts.length ? (
            filteredProducts.map((p) => <ProductCard key={p.id} product={p} />)
          ) : (
            <p className="opc-empty">产品信息完善中</p>
          )}
        </section>
      </div>
    </>
  );
}

/** 单维度筛选行（找 OPC 页内复用）：首个 tab 为「全部」（value=""）。 */
function FilterRow({
  title,
  field,
  values,
  value,
  onPick,
}: {
  title: string;
  field: string;
  values: string[];
  value: string;
  onPick: (v: string) => void;
}) {
  if (!values.length) return null;
  return (
    <div>
      <b>{title}</b>
      {["全部", ...values].map((label) => {
        const v = label === "全部" ? "" : label;
        return (
          <button
            key={label}
            data-field={field}
            data-value={v}
            className={v === value ? "active" : ""}
            onClick={() => onPick(v)}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
